import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronLeft, MenuIcon } from "lucide-react";
import { useState } from "react";
import { NocheLogo } from "@/components/NocheLogo";
import { useNavigate, useLocation } from "react-router-dom";
import { digitalIdItems, bottomItems } from "./sidebar/constants";
import { SidebarLink } from "./sidebar/SidebarLink";

interface DigitalIdSidebarProps {
  isCollapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

export const DigitalIdSidebar = ({ isCollapsed, onCollapse }: DigitalIdSidebarProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setIsScrolled(target.scrollTop > 10);
  };

  return (
    <>
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-3 rounded-full 
          backdrop-blur-xl bg-black/70 border border-neon-purple/40
          hover:border-neon-purple/70 hover:bg-black/80 hover:shadow-[0_0_25px_rgba(155,135,245,0.3)]
          transition-all duration-300 touch-manipulation active:scale-95"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <MenuIcon className="h-5 w-5 text-white" />
      </button>

      <div
        className={cn(
          "fixed inset-y-0 left-0 transition-all duration-500 flex flex-col z-40",
          "sidebar-gradient border-r border-neon-purple/30",
          "lg:shadow-[5px_0_25px_rgba(0,0,0,0.5)]",
          isCollapsed ? "w-[70px]" : "w-64",
          "lg:translate-x-0",
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="absolute top-0 left-0 w-full h-36 bg-gradient-to-b from-neon-purple/15 to-transparent opacity-60 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-full h-36 bg-gradient-to-t from-neon-purple/10 to-transparent opacity-30 pointer-events-none"></div>
        
        <div className="absolute top-1/2 -right-[100px] -translate-y-1/2 w-[100px] h-[300px] bg-neon-purple/10 rounded-full blur-3xl"></div>

        <div className={cn(
          "flex h-16 shrink-0 items-center justify-between px-4 transition-all duration-300",
          "sidebar-header border-b border-neon-purple/20",
          isScrolled && "shadow-md"
        )}>
          <div className="sidebar-logo-container">
            <NocheLogo collapsed={isCollapsed} />
            <div className="sidebar-logo-glow"></div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onCollapse(!isCollapsed)}
            className="flex-shrink-0 ml-2 lg:flex hidden hover:bg-white/10 hover:text-neon-purple transition-all duration-300 active:scale-95"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <MenuIcon className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
            <div className="absolute inset-0 rounded-full blur-md bg-neon-purple/0 hover:bg-neon-purple/10 transition-all duration-300 -z-10"></div>
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex-shrink-0 ml-2 lg:hidden hover:bg-white/10 hover:text-neon-purple transition-all duration-300 active:scale-95"
            title="Close menu"
          >
            <ChevronLeft className="h-5 w-5" />
            <div className="absolute inset-0 rounded-full blur-md bg-neon-purple/0 hover:bg-neon-purple/10 transition-all duration-300 -z-10"></div>
          </Button>
        </div>

        <ScrollArea 
          className="flex-1 overflow-y-auto overscroll-contain touch-manipulation px-1"
          onScroll={handleScroll}
        >
          <div className="space-y-1 p-2">
            {/* Main Navigation */}
            <div className="space-y-1">
              {digitalIdItems.map((item) => (
                <SidebarLink
                  key={item.url}
                  item={{
                    title: item.title,
                    href: item.url,
                    icon: item.icon,
                  }}
                  collapsed={isCollapsed}
                  isActive={pathname === item.url}
                />
              ))}
            </div>

            {/* Bottom Navigation */}
            <div className="pt-4 mt-4 border-t border-neon-purple/20">
              {bottomItems.map((item) => (
                <SidebarLink
                  key={item.href}
                  item={item}
                  collapsed={isCollapsed}
                  isActive={pathname === item.href}
                />
              ))}
            </div>
          </div>
          
          <div className={cn(
            "absolute top-16 inset-x-0 h-5 bg-gradient-to-b from-black/50 to-transparent pointer-events-none transition-opacity duration-300",
            isScrolled ? "opacity-100" : "opacity-0"
          )}></div>
        </ScrollArea>
      </div>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-lg z-30 animate-fade-in"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};